import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Music, Video, Users, ArrowRight, Play, Calendar, Eye, Heart, BookOpen, HeartHandshake, Quote, Sparkles } from 'lucide-react';
import GlassMusicPlayer from './GlassMusicPlayer';

export default function LandingPage({ 
  heroImageUrl, 
  gallery = [], 
  music = [], 
  videos = [], 
  members = [],
  schedules = [],
  loading,
  currentUser,
  onArtistRegisterClick,
  onBookScheduleClick,
  onDonateClick
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const heroBgSrc = !isMobile ? (heroImageUrl || '/images/hero.jpg') : '/images/mobile-bg-3.jpg';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div
      className="w-full min-h-screen relative text-white overflow-hidden font-sans antialiased"
      style={{
        backgroundColor: '#050505',
        backgroundImage: `
          radial-gradient(ellipse 70% 45% at 15% 0%, rgba(189, 159, 103, 0.12), transparent 60%),
          radial-gradient(ellipse 55% 40% at 85% 25%, rgba(208, 176, 122, 0.08), transparent 60%),
          radial-gradient(ellipse 60% 50% at 50% 100%, rgba(189, 159, 103, 0.08), transparent 60%)
        `,
      }}
    >
      {/* Ambient Animated Gold Glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <motion.div
          className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-[#bd9f67]/10 blur-3xl"
          animate={{ x: [0, 70, 0], y: [0, 50, 0] }}
          transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#bd9f67]/8 blur-3xl"
          animate={{ x: [0, -60, 0], y: [0, 70, 0] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-10 left-1/4 w-[28rem] h-[28rem] rounded-full bg-[#d0b07a]/8 blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, -40, 0] }}
          transition={{ duration: 34, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Hero Section */}
      <section 
        className="relative min-h-[100svh] flex items-center justify-center px-4 py-16 md:py-24 overflow-hidden"
      >
        {/* Background image */}
        <div className="absolute inset-0" aria-hidden="true">
          <motion.img
            src={heroBgSrc}
            alt=""
            className="w-full h-full object-cover object-center"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: imageLoaded ? 1 : 0, scale: 1.16 }}
            transition={{
              opacity: { duration: 0.8, ease: 'easeOut' },
              scale: { duration: 20, ease: 'easeOut' },
            }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/40" />
          {/* Subtle metallic tint */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#bd9f67]/10 via-transparent to-black/40 mix-blend-overlay" />
          {/* Bottom fade into section content */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
          {/* Vignette effect */}
          <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 160px rgba(0, 0, 0, 0.8)' }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 text-center text-white max-w-2xl mx-auto w-full"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="mb-4 md:mb-5 flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#bd9f67]/20 rounded-full blur-2xl" />
              <img
                src="/images/asidors_logo_white.png"
                alt="The Asidors Logo"
                className="h-20 sm:h-24 md:h-28 lg:h-36 drop-shadow-[0_10px_25px_rgba(0,0,0,0.8)] relative z-10"
              />
            </div>
          </motion.div>

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-6 md:mb-8"
          >
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-white/80 font-normal px-2 tracking-normal leading-relaxed">
              A family of gospel singers spreading messages of faith, hope, and love through music
            </p>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex flex-row flex-wrap gap-3 justify-center px-2 mb-8"
          >
            <a
              href="/music"
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#bd9f67] text-gray-950 rounded-xl font-semibold text-sm transition-all duration-200 hover:bg-[#d0b07a] shadow-lg shadow-[#bd9f67]/20"
            >
              <Music size={18} />
              Listen Now
            </a>
            <a
              href="/videos"
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 bg-white/5 text-white border border-white/10 backdrop-blur-md hover:bg-white/10 hover:border-white/20"
            >
              <Play size={18} />
              Watch Videos
            </a>
          </motion.div>

          {/* Music Player Container */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-8 flex items-center justify-center"
          >
            <GlassMusicPlayer music={music} />
          </motion.div>
        </motion.div>
      </section>

      {/* Explore & Ministry Callouts Section */}
      <section className="py-12 md:py-16 px-4 relative">
        <div className="max-w-6xl mx-auto relative z-10 space-y-8 md:space-y-12">

          {/* Ministration Callout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center text-white bg-[#121318]/70 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/10 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#bd9f67]/10 blur-2xl rounded-full pointer-events-none" />
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2 text-[#bd9f67]">
              Let's Lift His Name Together
            </h2>
            <p className="text-sm md:text-base mb-5 text-white/80 max-w-2xl mx-auto leading-relaxed">
              Ready to bring the sound of heaven to your community? We are available for ministrations, worship nights, and gospel events worldwide.
            </p>
            <div className="flex flex-row flex-wrap gap-3 justify-center px-2">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={onBookScheduleClick}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#bd9f67] text-gray-950 font-semibold rounded-xl text-sm transition-all duration-200 hover:bg-[#d0b07a] shadow-md"
              >
                <Calendar size={18} />
                Book a Schedule
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => window.location.href = '/schedules'}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 bg-white/5 text-white/90 border border-white/10 hover:bg-white/10"
              >
                <Eye size={18} />
                View Schedules
              </motion.button>
            </div>
          </motion.div>

          {/* Artist Registration CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center text-white bg-[#121318]/70 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/10 shadow-2xl"
          >
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2 text-white">
              Ready to Share Your Music?
            </h2>
            <p className="text-sm md:text-base mb-5 text-white/80 leading-relaxed">
              Join us as an artist and upload your gospel music to our platform.
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={onArtistRegisterClick}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 text-white border border-white/15 rounded-xl font-medium text-sm transition-all duration-200 backdrop-blur-md hover:bg-white/20 hover:border-white/30"
            >
              <Music size={18} className="text-[#bd9f67]" />
              Register as Artist
            </motion.button>
          </motion.div>

          {/* Donation Support Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center text-white bg-[#121318]/70 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/10 shadow-2xl"
          >
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#bd9f67]/15 rounded-full border border-[#bd9f67]/30 text-[#bd9f67]">
                <Heart size={22} />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2 text-white">
              Support Our Ministry
            </h2>
            <p className="text-sm md:text-base mb-5 text-white/80 leading-relaxed">
              Your generous donation helps us continue spreading the gospel message and bringing inspiration to communities worldwide.
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={onDonateClick}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#bd9f67] text-gray-950 rounded-xl font-semibold text-sm transition-all duration-200 hover:bg-[#d0b07a] shadow-md"
            >
              <Heart size={18} fill="currentColor" />
              Make a Donation
            </motion.button>
          </motion.div>

          {/* The Word & Faith Section */}
          <section className="pt-8 pb-4">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10 md:mb-14 px-2"
            >
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#bd9f67]/10 text-[#bd9f67] border border-[#bd9f67]/20 text-xs font-semibold tracking-wider uppercase mb-4 backdrop-blur-md">
                <Sparkles size={12} /> Faith • Hope • Love
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4 leading-tight">
                Messages That Reach The Heart
              </h2>
              <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
                We exist to lift up the name of Jesus — through worship, encouragement, and fellowship that points every soul back to His grace.
              </p>
            </motion.div>

            {/* Scripture Highlight Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative max-w-3xl mx-auto text-center text-white bg-[#121318]/80 backdrop-blur-xl rounded-2xl p-8 md:p-12 border border-[#bd9f67]/25 shadow-2xl mb-12 md:mb-16 overflow-hidden"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#bd9f67]/10 blur-3xl pointer-events-none" />
              <Quote size={32} className="text-[#bd9f67] mx-auto mb-5 relative" />
              <p className="relative text-xl md:text-2xl font-normal leading-relaxed text-white/90 mb-6">
                "Let everything that has breath praise the LORD. Praise ye the LORD."
              </p>
              <span className="relative inline-block text-xs md:text-sm text-[#bd9f67] font-semibold tracking-wider uppercase">
                Psalm 150:6
              </span>
            </motion.div>

            {/* Ministry Cards */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
            >
              {/* Worship & Music Card */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="group relative bg-[#121318]/70 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/10 hover:border-[#bd9f67]/40 transition-all duration-300 overflow-hidden shadow-xl"
              >
                <div className="w-fit p-3 bg-[#bd9f67]/10 rounded-xl border border-[#bd9f67]/20 group-hover:bg-[#bd9f67]/20 transition-colors mb-4">
                  <Music size={22} className="text-[#bd9f67]" />
                </div>
                <h3 className="text-base md:text-xl font-bold tracking-tight text-white mb-2">Worship & Music</h3>
                <p className="text-white/60 text-xs md:text-sm mb-6 leading-relaxed">
                  Immerse yourself in gospel songs and live worship that stir the heart and draw you closer to God.
                </p>
                <a
                  href="/music"
                  className="inline-flex items-center gap-2 text-[#bd9f67] font-semibold text-xs md:text-sm group/link hover:text-[#d0b07a] transition"
                >
                  Listen Now
                  <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                </a>
                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#bd9f67] to-transparent opacity-40" />
              </motion.div>

              {/* Messages of Hope Card */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="group relative bg-[#121318]/70 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/10 hover:border-[#bd9f67]/40 transition-all duration-300 overflow-hidden shadow-xl"
              >
                <div className="w-fit p-3 bg-[#bd9f67]/10 rounded-xl border border-[#bd9f67]/20 group-hover:bg-[#bd9f67]/20 transition-colors mb-4">
                  <BookOpen size={22} className="text-[#bd9f67]" />
                </div>
                <h3 className="text-base md:text-xl font-bold tracking-tight text-white mb-2">Messages of Hope</h3>
                <p className="text-white/60 text-xs md:text-sm mb-6 leading-relaxed">
                  Devotionals, testimonies, and words of encouragement to strengthen your faith and renew your spirit.
                </p>
                <a
                  href="/videos"
                  className="inline-flex items-center gap-2 text-[#bd9f67] font-semibold text-xs md:text-sm group/link hover:text-[#d0b07a] transition"
                >
                  Watch & Read
                  <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                </a>
                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#bd9f67] to-transparent opacity-40" />
              </motion.div>

              {/* Partner in Ministry Card */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="group relative bg-[#121318]/70 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/10 hover:border-[#bd9f67]/40 transition-all duration-300 overflow-hidden shadow-xl"
              >
                <div className="w-fit p-3 bg-[#bd9f67]/10 rounded-xl border border-[#bd9f67]/20 group-hover:bg-[#bd9f67]/20 transition-colors mb-4">
                  <HeartHandshake size={22} className="text-[#bd9f67]" />
                </div>
                <h3 className="text-base md:text-xl font-bold tracking-tight text-white mb-2">Partner in Ministry</h3>
                <p className="text-white/60 text-xs md:text-sm mb-6 leading-relaxed">
                  Support the mission or invite us to minister at your church, event, and worship night worldwide.
                </p>
                <button
                  onClick={onDonateClick}
                  className="inline-flex items-center gap-2 text-[#bd9f67] font-semibold text-xs md:text-sm group/link hover:text-[#d0b07a] transition"
                >
                  Support Ministry
                  <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                </button>
                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#bd9f67] to-transparent opacity-40" />
              </motion.div>
            </motion.div>
          </section>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-20 px-4 relative">
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-12 px-2"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-3">
              About The AsidorS Family
            </h2>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
              We are a family of gospel singers united in faith and purpose, dedicated to spreading messages of hope, love, and spiritual awakening through heartfelt performances.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6"
          >
            {[
              { icon: Music, title: 'Family Harmony', desc: 'United in faith, blending voices in harmony' },
              { icon: Video, title: 'Inspiring Performances', desc: 'Heartfelt live performances for all ages' },
              { icon: Users, title: 'A Ministry', desc: 'Spreading the gospel message worldwide' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="text-center bg-[#121318]/70 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-[#bd9f67]/30 transition-all duration-300 shadow-xl"
              >
                <div className="bg-[#bd9f67]/10 border border-[#bd9f67]/20 rounded-2xl w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <item.icon size={26} className="text-[#bd9f67]" />
                </div>
                <h3 className="text-base font-bold tracking-tight text-white mb-1.5">{item.title}</h3>
                <p className="text-white/60 text-xs md:text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}